/**
 * When a message starts a new group — V8-02 §1 [DECIDED, Teams/WhatsApp model].
 *
 * The founder's screenshots showed two defects that are really one: consecutive
 * messages never said who was speaking, and every bubble carried the same left
 * offset whether or not it needed an avatar beside it — so the text floated in
 * a wide dead gutter.
 *
 * Both come from treating every message as independent. Grouping is the fix:
 * the *first* message of a run carries the avatar and the name, and the rest
 * indent under it with neither. §1's rule, exactly:
 *
 *   a message starts a new group when any of —
 *     · a different sender
 *     · more than five minutes since the previous message
 *     · a day separator or a system/chip message intervenes
 *
 * Pure and separate from the component because it is the whole of the layout's
 * behaviour, it has four branches worth naming, and one of them is a clock.
 */

/** §1 — "more than 5 minutes since the previous message". */
export const GROUP_GAP_MS = 5 * 60 * 1000;

export interface GroupableMessage {
  _id: string;
  senderId?: string;
  kind: 'user' | 'system';
  createdAt: string | Date;
  /** A live task chip. §1 counts these as intervening, like a system line. */
  taskRef?: { taskKey?: string } | null;
}

/**
 * Does this message begin a new visual group?
 *
 * `previous` is the message rendered immediately above it — already filtered
 * and ordered by the caller, so "intervenes" is simply "is the one before".
 */
export function startsGroup(
  message: GroupableMessage,
  previous: GroupableMessage | undefined,
): boolean {
  // The first message on screen always starts one.
  if (!previous) return true;

  // A system line or a task chip breaks the run on either side: they are
  // centred, full-width furniture, and a bubble tucked under one reads as a
  // reply to it.
  if (message.kind === 'system' || previous.kind === 'system') return true;
  if (message.taskRef?.taskKey || previous.taskRef?.taskKey) return true;

  if ((message.senderId ?? '') !== (previous.senderId ?? '')) return true;

  const gap =
    new Date(message.createdAt).getTime() - new Date(previous.createdAt).getTime();
  // Strictly greater: five minutes exactly is still the same thought.
  if (gap > GROUP_GAP_MS) return true;

  /**
   * A day separator intervening.
   *
   * Derived from the dates rather than from the separator element, so the rule
   * holds however the separator happens to be rendered — the two cannot drift
   * apart, because there is only one fact and this is it.
   */
  return !isSameDay(new Date(message.createdAt), new Date(previous.createdAt));
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Is this the *last* message of its group?
 *
 * §1 puts the time and ticks on a group's last bubble for your own messages,
 * which needs looking forward as well as back.
 */
export function endsGroup(
  message: GroupableMessage,
  next: GroupableMessage | undefined,
): boolean {
  if (!next) return true;
  return startsGroup(next, message);
}
